import { ArrowUpRight, ArrowUpToLine, Ban, Check, CircleAlert, CircleX, GitBranch, Info, Layers, RotateCcw, TriangleAlert } from '@orgus/ui/icons';
import { useState } from 'react';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { LANDING_STAGES } from '@orgus/protocol';
import type { Id, Landing, LandingEta, LandingEventTone, LandingQueue, LandingStatus, Project } from '@orgus/protocol';
import { Button, ConfirmDialog, Drawer, ErrorState, GroupChevron, LogViewer, Pressable, RelativeTime, RowStatus, StatusPill, cn, formatDuration, formatInt, plural, toast, useNow } from '@orgus/ui';
import type { PillStatus } from '@orgus/ui';
import { cancelLanding, retryLanding } from './api.ts';
import { etaText, landingLabel, landingLogEmptyText } from './model.ts';
import { useJobLog } from './useJobLog.ts';
import { FactGrid } from '../detail/FactGrid.tsx';

const TONE_ICON: Record<LandingEventTone, typeof Info> = { info: Info, ok: Check, warn: TriangleAlert, error: CircleX };
const TONE_CLASS: Record<LandingEventTone, string> = { info: 'text-fg-subtle', ok: 'text-fg-subtle', warn: 'text-status-attention', error: 'text-status-danger' };

/** The landing's state as the drawer header's soft pill. */
const LANDING_PILL: Record<LandingStatus, PillStatus> = { queued: 'queued', active: 'working', parked: 'needs-you', failed: 'failed', landed: 'done', cancelled: 'ended' };

/** A title or label never ends in a full stop ("Verify failed.", from the hub, reads "Verify failed"). */
export function noStop(s: string): string {
  return s.trim().replace(/\.+$/, '');
}

/** A first word that names a command or a path keeps its case ("pnpm verify exited", "apps/x.ts"). */
const AS_WRITTEN = /^(?:pnpm|npm|npx|yarn|git|go|node|orgus|cargo|make)\b|^\S*[/.:_\d]/;

/** The first letter up: a reason the adapter wrote in lower case starts a sentence, unless it starts with a command or a path. */
export function sentence(s: string): string {
  const t = s.trim();
  return AS_WRITTEN.test(t) ? t : t.charAt(0).toUpperCase() + t.slice(1);
}

/** Durations the hub writes into event text ("3 min 12 s") in the one format the console uses ("3m 12s"). */
export function eventText(text: string): string {
  return text
    .replace(/\b(\d+) min (\d+) s\b/g, (_, m: string, s: string) => formatDuration((Number(m) * 60 + Number(s)) * 1000))
    .replace(/\b(\d+) min\b/g, (_, m: string) => formatDuration(Number(m) * 60_000))
    .replace(/\b(\d+) s\b/g, (_, s: string) => formatDuration(Number(s) * 1000));
}

/** How a run of stages ended, for the strip: still going, stopped for a person, failed, or all passed. */
export type StripOutcome = 'queued' | 'running' | 'parked' | 'failed' | 'passed' | 'cancelled';

/**
 * Where a change is in its stages (change, verify, push, deploy, landed for a landing; the
 * adapter's own for a pipeline): thin bars in the status fills. Stages behind it are grey, the
 * one it is in carries its state, and a run that passed every stage is the passed fill, as the
 * RunStrip draws it.
 */
export function StageStrip({ stages, current, outcome, currentLabel }: { stages: readonly { id: string; title: string }[]; current: string; outcome: StripOutcome; currentLabel: string }) {
  const at = Math.max(0, stages.findIndex((s) => s.id === current));
  const passed = outcome === 'passed';
  const hereFill: Record<StripOutcome, string> = {
    queued: 'bg-fg-faint',
    running: 'bg-status-working-fill',
    parked: 'bg-status-attention-fill',
    failed: 'bg-status-danger-fill',
    passed: 'bg-status-success-fill',
    cancelled: 'bg-fg-faint',
  };
  return (
    <ol className="flex items-center gap-1" aria-label="Stages">
      {stages.map((s, i) => {
        const done = passed || i < at;
        const here = !passed && i === at;
        return (
          <li key={s.id} className="flex min-w-0 flex-1 flex-col gap-1">
            <span className={cn('h-1 rounded-pill', passed ? 'bg-status-success-fill' : done ? 'bg-fg-subtle' : here ? hereFill[outcome] : 'bg-active')} aria-hidden />
            <span className={cn('truncate text-caption', here ? 'text-fg' : 'text-fg-subtle')}>
              {s.title}
              <span className="sr-only">{done ? ', done' : here ? `, ${currentLabel.toLowerCase()}` : ', not reached'}</span>
            </span>
          </li>
        );
      })}
    </ol>
  );
}

/**
 * Why a change stopped, as the drawers show it: a short title, at most a line or two of what to
 * do, then any files it names, once, in mono. Amber when it waits for a person, red when it failed.
 */
export function ProblemBanner({ tone, title, lines, files }: { tone: 'attention' | 'danger'; title: string; lines: readonly string[]; files?: readonly string[] }) {
  return (
    <div role="alert" className={cn('flex gap-3 rounded-card border bg-surface px-4 py-3', tone === 'attention' ? 'border-status-attention-line' : 'border-status-danger-line')}>
      <span className="flex h-5 shrink-0 items-center">
        {tone === 'attention' ? <CircleAlert className="size-4 text-status-attention" aria-hidden /> : <CircleX className="size-4 text-status-danger" aria-hidden />}
      </span>
      <div className="flex min-w-0 flex-col gap-1 text-ui">
        <p className="font-medium text-fg">{noStop(title)}</p>
        {lines.map((t) => (
          <p key={t} className="break-words text-fg-muted">
            {t}
          </p>
        ))}
        {files && files.length > 0 && (
          <ul className="mt-1 flex flex-col gap-1" aria-label="Files">
            {files.map((f) => (
              <li key={f} className="truncate font-mono text-caption text-fg" title={f}>
                {f}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

/**
 * A secondary section of a drawer, folded at rest: its title and a quiet summary, the chevron
 * after them, inside the content column (never hanging in the gutter).
 */
export function Fold({ title, aside, children }: { title: string; aside?: ReactNode; children: ReactNode }) {
  const [open, setOpen] = useState(false);
  return (
    <section aria-label={title} className="flex min-w-0 shrink-0 flex-col gap-2">
      <h3 className="flex h-control-sm min-w-0 items-center text-caption">
        <Pressable aria-expanded={open} onClick={() => setOpen((v) => !v)} className="flex w-auto min-w-0 max-w-full items-center gap-2 rounded-control text-fg-muted hover:text-fg">
          <span className="shrink-0 font-medium">{title}</span>
          {aside != null && <span className="min-w-0 truncate text-fg-subtle num">{aside}</span>}
          <GroupChevron open={open} />
        </Pressable>
      </h3>
      {open && children}
    </section>
  );
}

/** One entry of a drawer's timeline: what happened, how it went, and when. */
export interface TimelineEntry {
  tone: LandingEventTone;
  text: string;
  at: number;
}

/**
 * A drawer's timeline, folded at rest with its count: one line per event, its tone as a small
 * icon (grey info and done, amber warning, red failure), the text, and when. Both drawers (a
 * landing, a pipeline item) use it, so their histories read the same.
 */
export function Timeline({ events }: { events: readonly TimelineEntry[] }) {
  if (!events.length) return null;
  return (
    <Fold title="Timeline" aside={<span className="num">{formatInt(events.length)}</span>}>
      <ol className="flex flex-col gap-2">
        {events.map((e, i) => {
          const Icon = TONE_ICON[e.tone];
          return (
            <li key={`${e.at}-${i}`} className="flex items-start gap-2 text-ui">
              <span className="flex h-5 shrink-0 items-center">
                <Icon className={cn('size-4', TONE_CLASS[e.tone])} aria-hidden />
              </span>
              <span className="min-w-0 flex-1 break-words text-fg-muted">{eventText(e.text)}</span>
              <RelativeTime ts={e.at} className="flex h-5 shrink-0 items-center text-caption text-fg-subtle num" />
            </li>
          );
        })}
      </ol>
    </Fold>
  );
}

/**
 * The drawer's banner for a landing that stopped, in one short line under its title: a conflict
 * names its files once; anything else says why. What to do next is the footer's Retry and the log
 * below, so the hub's "Open the log, fix it, push it, then retry" is not repeated as prose.
 */
export function landingProblem(l: Pick<Landing, 'problem' | 'conflictFiles' | 'target'>): { title: string; lines: string[]; files: string[] } | null {
  if (!l.problem) return null;
  const files = l.conflictFiles ?? [];
  if (files.length) return { title: `Conflicts with ${l.target}`, lines: [], files };
  const why = l.problem.why ? sentence(noStop(l.problem.why)) : '';
  return { title: noStop(l.problem.what), lines: why ? [why] : [], files: [] };
}

/** The timeline's last entry when the events stop short of how the landing ended: its failure, or its landing. */
export function closingEvent(l: Pick<Landing, 'status' | 'problem' | 'events' | 'endedAt' | 'updatedAt' | 'target' | 'version'>): { tone: LandingEventTone; text: string; at: number } | null {
  const last = l.events.at(-1);
  const at = l.endedAt ?? l.updatedAt;
  if (l.status === 'failed' && last?.tone !== 'error') {
    const what = l.problem ? noStop(l.problem.what) : 'Failed';
    const why = l.problem?.why ? noStop(l.problem.why) : '';
    return { tone: 'error', text: why ? `${what}, ${why.charAt(0).toLowerCase()}${why.slice(1)}` : what, at };
  }
  if (l.status === 'landed' && last?.stage !== 'landed') return { tone: 'ok', text: l.version ? `Landed on ${l.target} as ${l.version}` : `Landed on ${l.target}`, at };
  return null;
}

/**
 * One landing (the drawer pattern): its status and branch in the header; why it stopped, short,
 * when it did; where it is now; the four facts that matter in the labelled grid; its verify or
 * deploy log. The rest (commits, conflicts, version) and the timeline rest folded.
 */
export function LandingDrawer({
  landing: l,
  queue,
  eta,
  project,
  verifyCommand,
  machineName,
  onClose,
}: {
  landing: Landing | undefined;
  queue: LandingQueue | undefined;
  eta: LandingEta | null;
  project: Project | undefined;
  verifyCommand: string;
  machineName: (id: Id | null) => string;
  onClose: () => void;
}) {
  const now = useNow(1000);
  const navigate = useNavigate();
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const open = !!l;
  // The job is the verify run, or the deploy once it deploys: at push the verify log has ended.
  const running = !!l && l.status === 'active' && l.stage !== 'push';
  const log = useJobLog(l?.jobId ?? null, open, { command: l?.stage === 'deploy' ? 'deploy' : verifyCommand || 'verify', failed: l?.status === 'failed', running });
  const canCancel = !!l && (l.status === 'queued' || l.status === 'parked' || l.status === 'failed' || (l.status === 'active' && (l.stage === 'change' || l.stage === 'verify'))) && !l.landedSha;
  const canRetry = !!l && (l.status === 'parked' || l.status === 'failed' || l.status === 'cancelled') && !l.landedSha;
  const problem = l ? landingProblem(l) : null;
  const hasLog = log.text.trim().length > 0 || log.live || log.loading || log.error != null;
  const closing = l ? closingEvent(l) : null;
  const outcome: StripOutcome = !l ? 'queued' : l.status === 'landed' ? 'passed' : l.status === 'active' ? 'running' : l.status === 'parked' ? 'parked' : l.status === 'failed' ? 'failed' : l.status === 'cancelled' ? 'cancelled' : 'queued';

  const [actError, setActError] = useState<unknown>(null);
  const act = async (name: string, fn: () => Promise<void>, done: string) => {
    setBusy(name);
    setActError(null);
    try {
      await fn();
      toast.success(done, l?.title);
    } catch (e) {
      setActError(e);
    } finally {
      setBusy(null);
    }
  };

  // Actions sit at the right in every state; with none, the footer does not render.
  const footer =
    l && (l.landedSha || canCancel || canRetry || l.incidentId) ? (
      <>
        {l.incidentId && (
          <Button variant="secondary" icon={<ArrowUpRight />} onClick={() => navigate(`/?incident=${encodeURIComponent(l.incidentId!)}`)}>
            Open incident
          </Button>
        )}
        <span className="flex-1" />
        {canCancel && (
          <Button variant="ghost" icon={<Ban />} onClick={() => setConfirmCancel(true)}>
            Cancel landing
          </Button>
        )}
        {l.landedSha && (
          <Button variant="secondary" icon={<Layers />} onClick={() => navigate(`/pipelines/environments?project=${encodeURIComponent(l.projectId)}`)}>
            Open environments
          </Button>
        )}
        {canRetry && (
          <>
            <Button variant="secondary" icon={<ArrowUpToLine />} loading={busy === 'front'} onClick={() => act('front', () => retryLanding(l.id, true), 'Retrying first in line')}>
              Retry first
            </Button>
            <Button variant="primary" icon={<RotateCcw />} loading={busy === 'retry'} onClick={() => act('retry', () => retryLanding(l.id), 'Back in the queue')}>
              Retry
            </Button>
          </>
        )}
      </>
    ) : null;

  return (
    <>
      <Drawer
        open={open}
        onOpenChange={(v) => !v && onClose()}
        width={640}
        title={l?.title ?? 'Landing'}
        eyebrow={
          l && (
            <>
              <StatusPill status={LANDING_PILL[l.status]} label={landingLabel(l)} />
              <span className="font-mono text-caption text-fg-subtle">{l.id}</span>
            </>
          )
        }
        description={
          l && (
            <span className="flex flex-wrap items-center gap-x-2">
              <span className="flex min-w-0 items-center gap-1">
                <GitBranch className="size-4 shrink-0" aria-hidden />
                <span className="truncate font-mono">{l.source.branch}</span>
                <span>to</span>
                <span className="font-mono">{l.target}</span>
              </span>
              <span>{project?.name ?? 'Unknown project'}</span>
            </span>
          )
        }
        footer={footer}
        bodyClassName="flex flex-col gap-6 px-6 py-4"
      >
        {l && (
          <>
            {actError != null && (
              <ErrorState
                variant="inline"
                error={actError}
                action={
                  <Button variant="ghost" size="sm" onClick={() => setActError(null)}>
                    Dismiss
                  </Button>
                }
              />
            )}
            {problem && <ProblemBanner tone={l.status === 'parked' ? 'attention' : 'danger'} title={problem.title} lines={problem.lines} files={problem.files} />}

            <div className="flex shrink-0 flex-col gap-3">
              {l.status === 'active' && <RowStatus tone="working" icon="working" label={l.step} className="whitespace-normal" />}
              {l.status === 'queued' && (
                <p className="text-ui text-fg-muted">
                  {eta ? (eta.position === 1 ? 'Next in line. ' : `Number ${formatInt(eta.position)} in line. `) : ''}
                  {etaText(eta, now, !!queue?.paused)}.{l.step.startsWith('Waiting:') ? ` ${l.step.slice(9)}` : ''}
                </p>
              )}
              <StageStrip stages={LANDING_STAGES} current={l.stage} outcome={outcome} currentLabel={landingLabel(l)} />
            </div>

            <FactGrid
              label="Landing facts"
              className="shrink-0"
              lines={[
                [
                  { label: 'By', value: l.submittedBy, title: l.submittedBy },
                  { label: 'Machine', value: l.machineId ? machineName(l.machineId) : 'Not picked yet' },
                  l.status === 'landed' && l.version ? { label: 'Landed as', value: l.version, mono: true } : { label: 'Submitted', value: <RelativeTime ts={l.submittedAt} /> },
                  { label: l.status === 'active' ? 'Running for' : 'Took', value: l.startedAt != null ? formatDuration((l.endedAt ?? now) - l.startedAt) : 'Not started' },
                ],
              ]}
            />

            {/* Only a landing that ran a job has a log: a change stopped at its rebase never started
                verify. A log that ended with no output is not drawn as an empty box: the timeline
                and the banner say how it went. */}
            {l.jobId && hasLog && (
              <section aria-labelledby="landing-log" className="flex shrink-0 flex-col">
                <h3 id="landing-log" className="mb-2 text-caption font-medium text-fg-muted">
                  {l.stage === 'deploy' ? 'Deploy log' : 'Verify log'}
                </h3>
                <LogViewer label={`Log for ${l.title}`} text={log.text} live={log.live} loading={log.loading} error={log.error} onRetry={log.retry} emptyText={landingLogEmptyText(running)} className="h-72" />
              </section>
            )}

            <Fold title="Details" aside={l.landedSha ? `on ${l.target}` : l.conflicts ? plural(l.conflicts, 'conflict') : undefined}>
              <FactGrid
                lines={[
                  [
                    // Only what is known: a fact that has not happened yet drops out rather than reading "Not yet".
                    { label: 'Verify runs', value: formatInt(l.attempts) },
                    { label: 'Rebased onto', value: l.baseSha ? l.baseSha.slice(0, 7) : null, mono: true },
                    { label: 'Verified commit', value: l.headSha ? l.headSha.slice(0, 7) : null, mono: true },
                    l.version ? { label: 'Version', value: l.version, mono: true } : { label: 'Conflicts', value: l.conflicts ? `${formatInt(l.conflicts)} of 2` : null },
                  ],
                  ...(l.incidentId
                    ? [
                        [
                          {
                            label: 'Incident',
                            value: (
                              <Button variant="ghost" size="sm" icon={<ArrowUpRight />} onClick={() => navigate(`/?incident=${encodeURIComponent(l.incidentId!)}`)}>
                                {l.incidentId}
                              </Button>
                            ),
                          },
                        ],
                      ]
                    : []),
                ]}
              />
            </Fold>

            <Timeline events={[...l.events, ...(closing ? [closing] : [])]} />
          </>
        )}
      </Drawer>

      <ConfirmDialog
        open={confirmCancel}
        onOpenChange={setConfirmCancel}
        title={`Cancel landing "${l?.title ?? ''}"?`}
        description={
          l?.status === 'active'
            ? `Verify stops on ${machineName(l.machineId)} and the landing leaves the queue. Nothing is pushed. You can retry it later.`
            : 'It leaves the queue and nothing is pushed. You can retry it later.'
        }
        confirmLabel="Cancel landing"
        onConfirm={async () => {
          if (!l) return;
          await cancelLanding(l.id);
          toast.success('Landing cancelled', l.title);
        }}
      />
    </>
  );
}
